import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import Filter from '@/common/models/query-params/Filter';
import * as FilterComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
import LogicalOperator from '@/common/models/query-params/FilterLogicalOperator.enum';
import { toServerDateTimeNoTimezone } from '@/globals';
import i18n from '@/vueI18n';

import LineItemStatus from './LineItemStatus.enum';

interface LineItemOption {
  value: string;
  name: string;
  logicalOperator?: LogicalOperator;
  filters?: Filter[];
}

export default class LineItemOptions {
  static getLocalizedOptionName(name: string): string {
    return capitalize(i18n.global.t(`states.${name}`));
  }

  static demandStatusOptions(): LineItemOption[] {
    return [
      {
        value: 'COMPLETED',
        name: LineItemOptions.getLocalizedOptionName('completed'),
        logicalOperator: LogicalOperator.AND,
        filters: [
          new Filter('state', [LineItemStatus.SUBMITTED], FilterComparisonOperator.EQUAL),
          new Filter(
            'mediaowner_approvals.state',
            [LineItemStatus.APPROVED],
            FilterComparisonOperator.NOT_EQUAL,
            LogicalOperator.NOT,
          ),
          new Filter(
            'flight_to',
            [toServerDateTimeNoTimezone(DateTime.local().startOf('day'))],
            FilterComparisonOperator.LESS_THAN,
          ),
        ],
      },
      {
        value: 'ACTIVE',
        name: LineItemOptions.getLocalizedOptionName('active'),
        logicalOperator: LogicalOperator.AND,
        filters: [
          new Filter('state', [LineItemStatus.SUBMITTED], FilterComparisonOperator.EQUAL),
          new Filter(
            'mediaowner_approvals.state',
            [LineItemStatus.APPROVED],
            FilterComparisonOperator.NOT_EQUAL,
            LogicalOperator.NOT,
          ),
          new Filter(
            'flight_to',
            [toServerDateTimeNoTimezone(DateTime.local().startOf('day'))],
            FilterComparisonOperator.GREATER_THAN_OR_EQUAL,
          ),
          new Filter(
            'flight_from',
            [toServerDateTimeNoTimezone(DateTime.local())],
            FilterComparisonOperator.LESS_THAN_OR_EQUAL,
          ),
          new Filter('run_state', ['P'], FilterComparisonOperator.EQUAL),
        ],
      },
      {
        value: 'UPCOMING',
        name: LineItemOptions.getLocalizedOptionName('upcoming'),
        logicalOperator: LogicalOperator.AND,
        filters: [
          new Filter('state', [LineItemStatus.SUBMITTED], FilterComparisonOperator.EQUAL),
          new Filter(
            'mediaowner_approvals.state',
            [LineItemStatus.APPROVED],
            FilterComparisonOperator.NOT_EQUAL,
            LogicalOperator.NOT,
          ),
          new Filter(
            'flight_from',
            [toServerDateTimeNoTimezone(DateTime.local())],
            FilterComparisonOperator.GREATER_THAN,
          ),
          new Filter('run_state', ['P'], FilterComparisonOperator.EQUAL),
        ],
      },
      {
        value: 'PAUSED',
        name: LineItemOptions.getLocalizedOptionName('paused'),
        logicalOperator: LogicalOperator.AND,
        filters: [
          new Filter('state', [LineItemStatus.SUBMITTED], FilterComparisonOperator.EQUAL),
          new Filter(
            'mediaowner_approvals.state',
            [LineItemStatus.APPROVED],
            FilterComparisonOperator.NOT_EQUAL,
            LogicalOperator.NOT,
          ),
          new Filter(
            'flight_to',
            [toServerDateTimeNoTimezone(DateTime.local().startOf('day'))],
            FilterComparisonOperator.GREATER_THAN_OR_EQUAL,
          ),
          new Filter('run_state', ['S'], FilterComparisonOperator.EQUAL),
        ],
      },
      {
        value: 'CANCELLED',
        name: LineItemOptions.getLocalizedOptionName('archived'),
        filters: [new Filter('state', [LineItemStatus.CANCELLED], FilterComparisonOperator.EQUAL)],
      },
      {
        value: 'DRAFT',
        name: LineItemOptions.getLocalizedOptionName('draft'),
        filters: [new Filter('state', [LineItemStatus.DRAFT], FilterComparisonOperator.EQUAL)],
      },
      {
        value: 'PENDING_APPROVAL',
        name: LineItemOptions.getLocalizedOptionName('pendingApproval'),
        logicalOperator: LogicalOperator.AND,
        filters: [
          new Filter('state', [LineItemStatus.SUBMITTED], FilterComparisonOperator.EQUAL),
          new Filter(
            'mediaowner_approvals.state',
            [LineItemStatus.PENDING_APPROVAL],
            FilterComparisonOperator.NOT_EQUAL,
            LogicalOperator.NOT,
          ),
        ],
      },
      {
        value: 'REJECTED',
        name: LineItemOptions.getLocalizedOptionName('rejected'),
        logicalOperator: LogicalOperator.AND,
        filters: [
          new Filter(
            'flight_to',
            [toServerDateTimeNoTimezone(DateTime.local().startOf('day'))],
            FilterComparisonOperator.GREATER_THAN_OR_EQUAL,
          ),
          new Filter('state', [LineItemStatus.SUBMITTED], FilterComparisonOperator.EQUAL),
          new Filter('mediaowner_approvals.state', [LineItemStatus.REJECTED], FilterComparisonOperator.EQUAL),
        ],
      },
    ].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }

  static supplyStatusOptions(): LineItemOption[] {
    return [
      {
        value: 'COMPLETED',
        name: LineItemOptions.getLocalizedOptionName('completed'),
        logicalOperator: LogicalOperator.AND,
        filters: [
          new Filter(
            'mediaowner_approvals.state',
            [LineItemStatus.APPROVED],
            FilterComparisonOperator.NOT_EQUAL,
            LogicalOperator.NOT,
          ),
          new Filter(
            'flight_to',
            [toServerDateTimeNoTimezone(DateTime.local().plus({ days: -1 }))],
            FilterComparisonOperator.LESS_THAN,
          ),
        ],
      },
      {
        value: 'PUBLISHED',
        name: LineItemOptions.getLocalizedOptionName('running'),
        logicalOperator: LogicalOperator.AND,
        filters: [
          new Filter('state', [LineItemStatus.SUBMITTED], FilterComparisonOperator.EQUAL),
          new Filter(
            'mediaowner_approvals.state',
            [LineItemStatus.APPROVED],
            FilterComparisonOperator.NOT_EQUAL,
            LogicalOperator.NOT,
          ),
          new Filter(
            'flight_to',
            [toServerDateTimeNoTimezone(DateTime.local().plus({ days: -1 }))],
            FilterComparisonOperator.GREATER_THAN_OR_EQUAL,
          ),
          new Filter(
            'flight_from',
            [toServerDateTimeNoTimezone(DateTime.local())],
            FilterComparisonOperator.LESS_THAN_OR_EQUAL,
          ),
        ],
      },
      {
        value: 'UPCOMING',
        name: LineItemOptions.getLocalizedOptionName('upcoming'),
        logicalOperator: LogicalOperator.AND,
        filters: [
          new Filter(
            'mediaowner_approvals.state',
            [LineItemStatus.APPROVED],
            FilterComparisonOperator.NOT_EQUAL,
            LogicalOperator.NOT,
          ),
          new Filter(
            'flight_from',
            [toServerDateTimeNoTimezone(DateTime.local())],
            FilterComparisonOperator.GREATER_THAN,
          ),
        ],
      },
      {
        value: LineItemStatus.PENDING_APPROVAL,
        name: LineItemOptions.getLocalizedOptionName('pendingApproval'),
        logicalOperator: LogicalOperator.AND,
        filters: [new Filter('mediaowner_approvals.state', [LineItemStatus.PENDING_APPROVAL])],
      },
      {
        value: LineItemStatus.REJECTED,
        name: LineItemOptions.getLocalizedOptionName('rejected'),
        logicalOperator: LogicalOperator.AND,
        filters: [new Filter('mediaowner_approvals.state', [LineItemStatus.REJECTED])],
      },
    ].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }

  static demandDefaultStatusOptions(): LineItemOption[] {
    return [];
  }

  static supplyDefaultStatusOptions(): LineItemOption[] {
    return [
      {
        value: 'PUBLISHED',
        name: LineItemOptions.getLocalizedOptionName('running'),
        logicalOperator: LogicalOperator.AND,
        filters: [
          new Filter(
            'mediaowner_approvals.state',
            [LineItemStatus.APPROVED],
            FilterComparisonOperator.NOT_EQUAL,
            LogicalOperator.NOT,
          ),
          new Filter(
            'flight_to',
            [toServerDateTimeNoTimezone(DateTime.local().plus({ days: -1 }))],
            FilterComparisonOperator.GREATER_THAN_OR_EQUAL,
          ),
          new Filter(
            'flight_from',
            [toServerDateTimeNoTimezone(DateTime.local())],
            FilterComparisonOperator.LESS_THAN_OR_EQUAL,
          ),
        ],
      },
    ];
  }
}
